import { FC, useCallback, useEffect } from 'react'
import type { BaseChartData } from '@/api/chart/chart.service'
import { PropertyName, TimeUnitName } from '../../../types/apiResponse/chartTypes'
import { PointMouseHandler, ResponsiveLine } from '@nivo/line'
import { TimeTooltip } from './tooltips/TimeTooltip'
import { convertIntToDate, getTimeFormat } from '../../../utils/chart/stringUtils'
import { makeSingleDomainData } from '../../../utils/chart/makeSingleDomainData'
import { useAvgDistDateStore } from '../../../store/chart/useAvgDistDateStore'
import { onSingleDomainClick } from '../../../utils/chart/chartOnClick'

export type SingleDomainPlotProps = {
  property: PropertyName
  timeUnit: TimeUnitName
  schoolColors: { [schoolId: string]: string }
  chartResponse: BaseChartData
  labels?: { [schoolId: string]: string }
}

export type XY = { x: number; y: number }

export const SingleDomainPlot: FC<SingleDomainPlotProps> = ({
  property,
  timeUnit,
  chartResponse,
  schoolColors,
  labels,
}) => {
  const data = makeSingleDomainData(chartResponse, schoolColors, labels)
  const { setDate } = useAvgDistDateStore()

  const curve = property === 'count' ? 'stepAfter' : 'monotoneX'
  const handlePointClick = useCallback<PointMouseHandler>(
    (point) => {
      if (property === 'averageWeight') {
        onSingleDomainClick(point, setDate)
      }
    },
    [property, setDate]
  )
  const timeFormat = getTimeFormat(timeUnit)

  useEffect(() => {
    if (property === 'averageWeight') {
      const lastDataItem = data.at(-1)
      if (lastDataItem) {
        const lastPoint = lastDataItem.data.at(-1)
        if (lastPoint) {
          setDate(lastPoint.x)
        }
      }
    }
  }, [])

  return (
    <ResponsiveLine
      data={
        property === 'biomass'
          ? data.map((item) => {
            return {
              ...item,
              data: item.data.map((v) => {
                return { x: v.x, y: v.y / 1000 }
              }),
            }
          })
          : data
      }
      margin={{ top: 50, right: 50, bottom: 50, left: 70 }}
      lineWidth={3}
      xScale={{
        type: 'time',
        min: convertIntToDate(chartResponse.xMin),
        max: convertIntToDate(chartResponse.xMax),
      }}
      yScale={{
        type: 'linear',
        min: property === 'biomass' ? chartResponse.yMin / 1000 : chartResponse.yMin,
        max: property === 'biomass' ? chartResponse.yMax / 1000 : chartResponse.yMax,
        reverse: false,
      }}
      colors={(d) => d.color}
      axisBottom={{
        format: timeFormat,
        tickSize: 5,
        tickValues: chartResponse.xTicks.map((tick) => convertIntToDate(tick)),
        tickRotation: 30,
      }}
      axisLeft={{
        tickSize: 5,
        tickValues:
          property === 'biomass'
            ? chartResponse.yTicks.map((tick) => tick / 1000)
            : chartResponse.yTicks,
        format: (value) => {
          if (property === 'averageWeight') {
            return `${value} g`
          }
          if (property === 'sampleWeight') {
            return `${value} kg`
          }
          if (property === 'feedingAmount') {
            return `${value} kg`
          }
          if (property === 'biomass') {
            return `${value} kg`
          }
          if (property === 'nh3no2') {
            // NO2, NH3 등 소수점이 긴 값들을 소수점 2자리로 제한
            return Number(value).toFixed(2)
          }
          return value.toString()
        },
      }}
      curve={curve}
      pointSize={0}
      enableSlices="x"
      enableGridX={false}
      enableGridY={false}
      sliceTooltip={(p) => <TimeTooltip item={p} />}
      onClick={handlePointClick}
      legends={[
        {
          anchor: 'right',
          direction: 'column',
          justify: false,
          translateX: 70,
          translateY: 0,
          itemsSpacing: 4,
          itemDirection: 'left-to-right',
          itemWidth: 120,
          itemHeight: 16,
          symbolSize: 10,
          symbolShape: 'circle',
          toggleSerie: true,
        },
      ]}
    />
  )
}
